import {
  AfterContentInit,
  Directive,
  ElementRef,
  HostListener,
  Input,
  Output,
  OnChanges,
  OnDestroy,
  SimpleChanges, EventEmitter
} from '@angular/core';
import { ResizeSensor } from 'css-element-queries';
import isUndefined from 'lodash.isundefined';
import { NUMBER_15 } from '~shared/constants';
import { FLEX_START, FLEX_END } from '~shared/directives/sticky/skicky.constant';

@Directive({
  selector: '[appSticky]'
})
export class StickyDirective implements OnChanges, AfterContentInit, OnDestroy {
  @Input() offset = NUMBER_15;
  @Input() pinningPossible = false;
  @Input() stickTo: HTMLElement;
  @Input() needToRecalculate = false;
  @Output() stickyStatusChanged = new EventEmitter<boolean>();
  isPinned = false;
  isStick = false;

  resizeSubscription: ResizeSensor;
  elementResizeSubscription: ResizeSensor;
  element: HTMLElement;
  elementHeight: number;
  parentElement: HTMLElement;
  parentElementHeight: number;
  parentElementOffsetTop: number;
  currentScrollPosition: number;
  duration: number;

  constructor(private el: ElementRef) {
  }

  ngAfterContentInit() {
    this.init();
  }

  ngOnChanges(changes: SimpleChanges) {
    const { pinningPossible: { currentValue: isPossible } = {currentValue: false} } = changes;

    if (!isPossible && this.isPinned) {
      this.unPinElement();
    }

    if (this.needToRecalculate) {
      this.updateScene();
      this.needToRecalculate = false;
    }
  }

  ngOnDestroy() {
    this.resizeSubscription.detach();
    this.elementResizeSubscription.detach();
  }

  private init() {
    this.element = this.el.nativeElement;
    this.parentElement = isUndefined(this.stickTo) ? this.element.parentElement : this.stickTo;
    this.elementHeight = this.element.offsetHeight;
    this.parentElementHeight = this.parentElement.offsetHeight;
    this.parentElementOffsetTop = this.parentElement.offsetTop - this.offset;
    this.duration = this.parentElementHeight - this.elementHeight;

    this.resizeSubscription = new ResizeSensor(this.parentElement, () => this.updateScene());
    this.elementResizeSubscription = new ResizeSensor(this.element, () => this.updateScene());
  }

  private updateScene() {
    if (this.element && this.parentElement) {
      this.elementHeight = this.element.offsetHeight;
      this.parentElementHeight = this.parentElement.offsetHeight;
      this.parentElementOffsetTop = this.parentElement.offsetTop - this.offset;
      this.duration = this.parentElementHeight - this.elementHeight;
      this.onScroll();
    }
  }

  private pinElement() {
    if (!this.isPinned) {
      this.element.style.position = 'fixed';
      this.element.style.top = `${this.offset}px`;
      this.element.style.width = '100%';
      this.element.style.maxWidth = `${this.parentElement.offsetWidth}px`;
      this.isPinned = true;
      this.stickyStatusChanged.emit(true);
    }
  }

  private unPinElement() {
    if (this.isPinned) {
      this.parentElement.style.height = 'auto';
      this.element.style.alignSelf = FLEX_START;
      this.element.style.position = 'relative';
      this.element.style.maxWidth = '100%';
      this.element.style.top = 'auto';
      this.isPinned = false;
      this.isStick = false;
      this.stickyStatusChanged.emit(false);
    }
  }

  @HostListener('window:scroll', ['$event'])
  private onScroll() {
    this.currentScrollPosition = window.scrollY;

    const pinCondition = (this.currentScrollPosition > this.parentElementOffsetTop)
      && !(this.currentScrollPosition > (this.parentElementOffsetTop + this.duration));
    const unPinCondition = (this.currentScrollPosition > (this.parentElementOffsetTop + this.duration))
      || (this.currentScrollPosition < this.parentElementOffsetTop);
    const stickCondition = this.currentScrollPosition > (this.parentElementOffsetTop + this.duration);

    if (pinCondition) {
      this.pinElement();
    } else if (unPinCondition) {
      this.unPinElement();
    }

    if (stickCondition) {
      this.stickToBottom();
    } else {
      this.resetBottomStick();
    }
  }

  private stickToBottom() {
    const buyboxStartToken = 'product-details__buybox__start';
    if (!this.isStick) {
      if (this.element.classList.contains(buyboxStartToken)) {
        this.element.classList.remove(buyboxStartToken);
      }
      this.element.style.position = 'relative';
      this.element.style.alignSelf = FLEX_END;
      this.isStick = true;
    }
  }

  private resetBottomStick() {
    this.element.style.alignSelf = FLEX_START;
  }
}
